import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { HelpLink } from "../help/links";
import { fetchCounterparties, type Counterparty } from "../lib/counterparties";
import {
  canRetryParsing,
  checkDraft,
  confirmDraft,
  draftStatusLabel,
  draftFileUrl,
  draftFileViewUrl,
  fetchDraftCard,
  fetchDrafts,
  isWithdrawable,
  rejectDraft,
  releaseDraftInWork,
  retryDraft,
  takeDraftInWork,
  withdrawDraft,
  DRAFT_STATUS_HINT,
  DRAFT_STATUS_LABEL,
  type DraftCard,
  type DraftView,
  type DraftWarning,
  type PrefillObligation,
  type PrefillParty,
  type PrefillField,
  type DraftFileView,
  VIEWABLE_TYPES,
} from "../lib/drafts";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { InWorkStamp } from "../components/InWorkStamp";
import { DemoModeBanner } from "../components/DemoModeBanner";
import {
  DAY_TYPE_LABELS,
  DEBTOR_LABELS,
  OBLIGATION_TYPE_LABELS,
  RULE_BASE_LABELS,
  RULE_KIND_LABELS,
  type ObligationDayType,
  type ObligationDebtor,
  type ObligationRuleBase,
  type ObligationRuleKind,
  type ObligationType,
} from "../lib/obligations";

/**
 * Экран «Верификация» (E11, PR 9): юрист проверяет разбор и ставит договор на контроль.
 *
 * ПОРЯДОК ЭКРАНА НЕ СЛУЧАЕН (план §3.11).
 *
 * 1. ЗАМЕЧАНИЯ МОДЕЛИ — ВВЕРХУ. Расхождение договора со спецификацией и протокол разногласий это
 *    готовая работа юриста, и терять её в общем списке полей нельзя: в спайке такие находки
 *    были у двух документов из шести. С E11b там не только противоречия — отсутствие неустойки,
 *    рамочный характер договора, гарантийный срок, — отсюда заголовок «Замечания модели»
 *    (решение владельца 16.10.2026).
 * 2. КОНТРАГЕНТ — ПЕРВОЕ ПОЛЕ. Договор без контрагента не существует, а справочник отвергает
 *    негодный ИНН жёстко. Узнать об этом юрист должен ДО того, как разложил обязательства, —
 *    поэтому проверка зовётся сразу и по частичному телу.
 * 3. СПЕЦИФИКАЦИИ ДО ОБЯЗАТЕЛЬСТВ. Правило срока «от даты спецификации» не к чему привязать,
 *    пока спецификации нет.
 *
 * ЧТО ЭКРАН НЕ ДЕЛАЕТ. Он не подставляет за юриста ничего, чего не было в документе: несведённое
 * значение остаётся пустым и показывается с причиной, а подсказка срока приводится ровно так,
 * как её дала цитата. Ответственность за раскладку и сроки несёт верифицирующий юрист (ТЗ 4.2.3),
 * и экран обязан делать эту границу видимой, а не размывать её удобством. Обязательство-триггер,
 * подобранное сервером по словам события («с момента предоплаты» → предоплата, E11b PR-5), — это
 * предложение в обычном выпадающем списке: юрист видит его и меняет одним выбором.
 *
 * ГЛАВНЫЙ УРОК ПРИЁМКИ 16.08.2026, ИЗ КОТОРОГО ПЕРЕПИСАНА ПОЛОВИНА ЭТОГО ФАЙЛА. Владелец дошёл
 * до кнопки «Подтвердить и поставить на контроль» и получил отказ «specifications.0.date: Дата в
 * формате ГГГГ-ММ-ДД». Причина не в схеме: схема права, дата спецификации обязательна с E5, от
 * неё считается правило срока «от даты спецификации». Причина в том, что ПОЛЯ, КУДА ЭТУ ДАТУ
 * ВПИСАТЬ, НА ЭКРАНЕ НЕ БЫЛО. Экран показывал разбор и отправлял его как есть — а разбор неполон
 * по определению: модель не находит всего.
 *
 * Отсюда правило, по которому теперь устроен весь экран: ЕСЛИ СЕРВЕР ЧЕГО-ТО ТРЕБУЕТ, ЭКРАН
 * ОБЯЗАН ДАВАТЬ ЭТО ВВЕСТИ — и не пускать к кнопке, пока не введено. Пустое обязательное поле это
 * не «ошибка отправки», а незаконченная работа юриста, и сказать об этом надо до кнопки, рядом с
 * самим полем. Приём тот же, что с должником (E11a), и он распространён на все поля, которых
 * разбор может не дать: номер и дата спецификации, тип обязательства, описание и правило срока
 * целиком.
 *
 * ЧТО ИЗМЕНИЛА D-24. Экран стал КАРТОЧКОЙ КОНКРЕТНОГО ЧЕРНОВИКА по адресу `/verification/:id`, а
 * не «первым готовым из очереди». Прежнее устройство и было тем пробелом, из-за которого из трёх
 * загруженных комплектов владелец видел один: очередь теперь живёт отдельным экраном-списком, а
 * сюда приходят по ссылке — из списка либо из сигнала колокольчика.
 *
 * Отсюда же захват: раз черновик открывают адресно, его могут открыть двое одновременно. Кто взял
 * его в работу — сказано вверху, и второму юристу кнопки решения закрыты. ЧИТАТЬ он вправе:
 * сверить разбор со сканом — не то же самое, что поставить договор на контроль.
 */

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly card: DraftCard; readonly demoNotice: string }
  | { readonly kind: "error"; readonly message: string };

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
/** Правка юриста подсвечивается (принятое отступление дизайна Д-4). */
const changedCls = "border-accent bg-control-bg";
/** Обязательное поле, которого разбор не дал: юрист обязан его заполнить. */
const missingCls = "border-overdue-fg bg-overdue-bg";
const labelCls = "block text-[13px] font-bold text-ink";

/** Поля карточки договора в том виде, в каком их правит юрист (строками, как в форме). */
interface ContractForm {
  number: string;
  date: string;
  subject: string;
  validityTerm: string;
  direction: string;
  price: string;
  penPayRate: string;
  penDelRate: string;
  claimRequired: boolean;
  claimDays: string;
  court: string;
}

/**
 * Спецификация в форме. Номер и дата — ОБЯЗАТЕЛЬНЫЕ поля договора (E5), и модель их не всегда
 * находит: на приёмке 16.08.2026 живой Qwen вернул «№ 1 от —».
 */
interface SpecForm {
  number: string;
  date: string;
}

/**
 * Обязательство в форме — всё, что требуется, чтобы поставить его на контроль.
 *
 * ПОЧЕМУ ЗДЕСЬ ПОЛНОЦЕННОЕ ПРАВИЛО СРОКА, А НЕ ТОЛЬКО ВЫБОР ТРИГГЕРА. Модель почти всегда
 * возвращает вид правила «от события» (во всех фикстурах прогона 14.08 — кроме одного), но
 * событие она ОПИСЫВАЕТ СЛОВАМИ: «с даты подписания настоящего Договора», «с даты поступления
 * аванса». Первое — вовсе не событие в смысле системы, а отсчёт от даты договора; второе —
 * событие, и триггером ему служит другое обязательство. Различить их может только юрист, и для
 * этого ему нужен и выбор вида правила, и всё, что каждый вид требует.
 */
interface OblForm {
  type: ObligationType | "";
  descr: string;
  debtor: ObligationDebtor | "";
  ruleKind: ObligationRuleKind | "";
  ruleDate: string;
  ruleDays: string;
  dayType: ObligationDayType | "";
  ruleBase: ObligationRuleBase | "";
  /** Место спецификации в списке разбора; `null` — не привязано. */
  specIndex: number | null;
  /** Место обязательства-триггера в списке разбора; `null` — не выбрано. */
  triggerIndex: number | null;
}

function initialForm(card: DraftCard): ContractForm {
  const p = card.prefill;
  return {
    number: p?.number.value ?? "",
    date: p?.date.value ?? "",
    subject: p?.subject.value ?? "",
    validityTerm: p?.validityTerm.value ?? "",
    direction: p?.direction.value ?? "",
    price: p?.price.value ?? "",
    penPayRate: p?.penPayRate.value ?? "",
    penDelRate: p?.penDelRate.value ?? "",
    claimRequired: p?.claimRequired.value ?? false,
    claimDays: p?.claimDays.value === null || p === null ? "" : String(p.claimDays.value),
    court: p?.court.value ?? "",
  };
}

/**
 * Обязательство из разбора → поля формы. Чего модель не дала, остаётся пустым (план §3.6).
 *
 * ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ — СПЕЦИФИКАЦИЯ, КОГДА ОНА ОДНА. Модель говорит «срок считается от даты
 * спецификации», но ССЫЛКУ на конкретную спецификацию не даёт — её в ответе просто нет. Если
 * спецификация в комплекте ровно одна, выбирать не из чего: это не догадка, а единственное
 * возможное значение. Спецификаций две и больше — поле остаётся пустым, и выбирает юрист:
 * вот там догадка уже меняла бы дату срока.
 */
function initialObl(o: PrefillObligation, specCount: number): OblForm {
  return {
    type: (o.type ?? "") as ObligationType | "",
    descr: o.descr ?? "",
    debtor: o.debtor === "we" || o.debtor === "they" ? o.debtor : "",
    ruleKind: (o.ruleKind ?? "") as ObligationRuleKind | "",
    ruleDate: o.ruleDate ?? "",
    ruleDays: o.ruleDays === null ? "" : String(o.ruleDays),
    dayType: (o.dayType ?? "") as ObligationDayType | "",
    ruleBase: (o.ruleBase ?? "") as ObligationRuleBase | "",
    specIndex: o.ruleBase === "spec" && specCount === 1 ? 0 : null,
    // Подобранный сервером триггер (E11b PR-5) — только когда кандидат один; иначе пусто.
    triggerIndex: o.triggerIndex ?? null,
  };
}

export function VerifyPage() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [form, setForm] = useState<ContractForm | null>(null);
  const [initial, setInitial] = useState<ContractForm | null>(null);
  const [useExisting, setUseExisting] = useState<string>("");
  const [cpName, setCpName] = useState("");
  const [cpInn, setCpInn] = useState("");
  const [cpOgrn, setCpOgrn] = useState("");
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [specsTaken, setSpecsTaken] = useState<boolean[]>([]);
  const [specForms, setSpecForms] = useState<SpecForm[]>([]);
  const [oblTaken, setOblTaken] = useState<boolean[]>([]);
  const [oblForms, setOblForms] = useState<OblForm[]>([]);
  const [warnings, setWarnings] = useState<DraftWarning[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  /**
   * Черновик назван АДРЕСОМ (`/verification/:id`, D-24). Сюда ведут и строка списка, и сигнал
   * колокольчика: «откройте верификацию и найдите нужный» — это работа, которую система должна
   * делать сама, а при нескольких черновиках в очереди она была невыполнима.
   */
  const { id: wantedId } = useParams<{ id: string }>();

  const load = useCallback(async () => {
    if (wantedId === undefined) {
      setState({ kind: "error", message: "Черновик не указан. Откройте его из списка." });
      return;
    }
    const card = await fetchDraftCard(wantedId);
    if (!card.ok) {
      setState({ kind: "error", message: card.message });
      return;
    }
    // Очередь спрашивается ТОЛЬКО ради плашки демонстрационного режима (CR-4): она про состояние
    // разбора в целом, а не про этот черновик, и другого источника у неё нет. Отказ здесь не
    // мешает работать с карточкой — плашки просто не будет.
    const queue = await fetchDrafts();
    const start = initialForm(card.value);
    setState({
      kind: "ready",
      card: card.value,
      demoNotice: queue.ok ? queue.value.ai.demoNotice : "",
    });
    setForm(start);
    setInitial(start);
    setCpName(card.value.prefill?.counterparty.name ?? "");
    setCpInn(card.value.prefill?.counterparty.inn ?? "");
    setCpOgrn(card.value.prefill?.counterparty.ogrn ?? "");
    setUseExisting("");
    const specs = card.value.prefill?.specifications ?? [];
    setSpecsTaken(specs.map(() => true));
    setSpecForms(specs.map((s) => ({ number: s.number ?? "", date: s.date ?? "" })));
    const obls = card.value.prefill?.obligations ?? [];
    setOblTaken(obls.map(() => false));
    setOblForms(obls.map((o) => initialObl(o, specs.length)));
    setError(null);
  }, [wantedId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void fetchCounterparties().then((r) => {
      if (r.ok) setCounterparties(r.value);
    });
  }, []);

  const draftId = state.kind === "ready" ? state.card.draft.id : null;

  /**
   * Спрашивать сервер о препятствиях есть смысл только у ГОТОВОГО разбора: подтверждённый
   * черновик открыт для чтения, править его нечем, и проверка вернула бы отказ по статусу.
   */
  const checkableId =
    state.kind === "ready" && state.card.draft.status === "ready" ? state.card.draft.id : null;

  /** Тело проверки — ЧАСТИЧНОЕ: то, что юрист успел заполнить. */
  const partialBody = useCallback(() => {
    return {
      counterparty:
        useExisting === ""
          ? { create: { name: cpName, inn: cpInn, ogrn: cpOgrn } }
          : { id: useExisting },
      contract: { number: form?.number ?? "" },
    };
  }, [useExisting, cpName, cpInn, cpOgrn, form]);

  // Замечания пересчитываются, пока юрист правит поля: препятствие обязано всплыть ДО кнопки.
  useEffect(() => {
    if (checkableId === null) return;
    const timer = setTimeout(() => {
      void checkDraft(checkableId, partialBody()).then((r) => {
        if (r.ok) setWarnings(r.value);
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [checkableId, partialBody]);

  if (state.kind === "loading") {
    return (
      <Shell busy>
        <p className="mt-4 text-muted">Загрузка…</p>
      </Shell>
    );
  }
  if (state.kind === "error") {
    return (
      <Shell>
        <p role="alert" className="mt-4 text-overdue-fg">
          {state.message}
        </p>
      </Shell>
    );
  }
  /**
   * Повтор и отзыв объявлены ДО ветки «верифицировать нечего»: именно там они и нужны, а `card`
   * ниже в этой ветке недостижим. Работают по `draftId`, снятому вместе с состоянием.
   */
  async function onRetry(): Promise<void> {
    if (draftId === null) return;
    const agreed = window.confirm(
      "Повторить разбор? Это новый платный запрос к модели.\n\n" +
        "Повтор помогает, если отказ был временным («высокий спрос», обрыв связи). " +
        "Если причина в настройках или доступе, повтор её не исправит.",
    );
    if (!agreed) return;
    setBusy(true);
    setError(null);
    const result = await retryDraft(draftId);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    await load();
  }

  async function onWithdraw(): Promise<void> {
    if (draftId === null) return;
    setBusy(true);
    setError(null);
    const result = await withdrawDraft(draftId, withdrawReason);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setWithdrawReason("");
    await load();
  }

  /**
   * ЧЕРНОВИК ОТКРЫТ, НО ВЕРИФИЦИРОВАТЬ ЕГО НЕЛЬЗЯ — отдельное честное состояние (D-24).
   *
   * Раньше сюда попадали только готовые разборы, и на всё прочее экран отвечал «черновиков на
   * верификацию нет» — утверждение неверное и сбивающее с толку: черновик есть, просто он ещё
   * разбирается, уже подтверждён или упал. По ссылке из сигнала это выглядело как «сигнал был, а
   * работы нет». Теперь состояние называется, а рядом стоит то действие, которое из него ведёт.
   */
  /*
   * ПОДТВЕРЖДЁННЫЙ ЧЕРНОВИК ОТКРЫВАЕТСЯ ТОЛЬКО ДЛЯ ЧТЕНИЯ (E17, решение владельца 17.09.2026).
   *
   * Сигнал «Раскладка подтверждена» ведёт юриста второй ссылкой сюда — посмотреть, что предложила
   * модель, и сверить со сканом. Без этой ветки ссылка приводила бы на строку состояния, где нет
   * ни одного поля: выборочная проверка, ради которой сигнал и заведён, была бы невозможна.
   *
   * Поля показываются ровно те же, что видел Инициатор, и все закрыты: черновик после
   * подтверждения неизменяем, а правит юрист ДОГОВОР — туда и ведёт кнопка.
   */
  const readOnly = state.card.draft.status === "confirmed" && state.card.prefill !== null;

  if (state.card.draft.status !== "ready" && !readOnly) {
    return (
      <Shell>
        <NotReady
          card={state.card}
          mine={state.card.draft.createdById === (user?.id ?? null)}
          busy={busy}
          error={error}
          reason={withdrawReason}
          onReason={setWithdrawReason}
          onRetry={() => void onRetry()}
          onWithdraw={() => void onWithdraw()}
        />
      </Shell>
    );
  }
  if (form === null || initial === null) {
    return (
      <Shell busy>
        <p className="mt-4 text-muted">Загрузка…</p>
      </Shell>
    );
  }

  const { card } = state;
  const prefill = card.prefill;
  const specs = prefill?.specifications ?? [];
  const obls = prefill?.obligations ?? [];
  /**
   * Черновик держит ДРУГОЙ юрист — решения закрыты (D-24, пункт «г»).
   *
   * Свободный черновик подтверждать МОЖНО и без захвата: захват — про вежливость и видимость, а
   * не про разрешение. Требовать нажатия кнопки там, где очередь пуста и юрист один, значило бы
   * добавить обязательный шаг ради случая, которого в этот момент нет.
   */
  const heldByOther =
    card.draft.inWorkById !== null && card.draft.inWorkById !== (user?.id ?? null);

  /**
   * ЧЕГО НЕ ХВАТАЕТ ДЛЯ ПОСТАНОВКИ НА КОНТРОЛЬ — считается здесь, до кнопки.
   *
   * Проверяется ровно то же, что проверит сервер (`drafts/confirm.ts` и `validateRule`), и ровно
   * у того, что юрист ОТМЕТИЛ: неотмеченная строка никуда не уходит, и требовать от неё полноты
   * значило бы заставлять заполнять то, что решили не брать.
   *
   * У каждого замечания свой код с номером строки: три спецификации без даты — это три разных
   * препятствия, и общий код склеил бы их в одно (список рисуется по коду как по ключу).
   */
  const localWarnings: DraftWarning[] = [];

  specs.forEach((spec, i) => {
    if (!(specsTaken[i] ?? false)) return;
    const f = specForms[i];
    const name = specName(spec.number ?? f?.number ?? "", i);
    if ((f?.number ?? "").trim() === "") {
      localWarnings.push({
        code: `local:spec-number-${i}`,
        message: `${name}: не заполнен номер. Модель его не нашла — посмотрите в скане.`,
        blocking: true,
      });
    }
    if ((f?.date ?? "").trim() === "") {
      localWarnings.push({
        code: `local:spec-date-${i}`,
        message:
          `${name}: не заполнена дата. Модель её не нашла — посмотрите в скане. От даты ` +
          "спецификации считаются сроки обязательств по ней, поэтому без неё на контроль не ставим.",
        blocking: true,
      });
    }
  });

  obls.forEach((_, i) => {
    if (!(oblTaken[i] ?? false)) return;
    const f = oblForms[i];
    if (f === undefined) return;
    const name = oblName(f.descr, i);
    const need = (what: string, suffix = "") =>
      localWarnings.push({
        code: `local:obl-${what}-${i}`,
        message: `${name}: ${suffix}`,
        blocking: true,
      });

    if (f.descr.trim() === "")
      need("descr", "не заполнено описание — по нему обязательство ищут в реестре.");
    if (f.type === "")
      need("type", "не выбран тип обязательства — от него зависит ответственная роль.");
    if (f.debtor === "")
      need(
        "debtor",
        "не выбран должник. Укажите, кто его исполняет: от этого зависит, кому считается " +
          "просрочка и в чью пользу неустойка.",
      );
    if (f.ruleKind === "") {
      need("rulekind", "не выбран вид правила срока — модель его не определила.");
      return;
    }
    if (f.ruleKind === "fixed" && f.ruleDate.trim() === "") {
      need("ruledate", "правило «до конкретной даты», а сама дата не указана.");
    }
    if (f.ruleKind !== "fixed") {
      if (f.ruleDays.trim() === "") need("ruledays", "не указано число дней срока.");
      if (f.dayType === "")
        need(
          "daytype",
          "не выбран тип дней. Календарные, рабочие и банковские дают РАЗНЫЕ даты срока, и " +
            "по умолчанию система его не подставляет.",
        );
    }
    if (f.ruleKind === "relative") {
      if (f.ruleBase === "") need("rulebase", "не выбрано, от чего отсчитывается срок.");
      if (f.ruleBase === "spec" && f.specIndex === null) {
        need("rulespec", "срок отсчитывается от спецификации, а сама спецификация не выбрана.");
      }
    }
    if (f.ruleKind === "event") {
      if (f.triggerIndex === null) {
        need(
          "trigger",
          "не выбрано обязательство-триггер. Модель описала событие словами; чьим исполнением " +
            "оно наступает, решает юрист.",
        );
      } else if (f.triggerIndex === i) {
        need(
          "trigger-self",
          "событием-триггером указано оно само — выберите другое обязательство.",
        );
      } else if (!(oblTaken[f.triggerIndex] ?? false)) {
        need(
          "trigger-off",
          "обязательство-триггер не отмечено к постановке на контроль. Отметьте и его — без " +
            "него срок этого обязательства не от чего отсчитывать.",
        );
      }
    }
    if (f.specIndex !== null && !(specsTaken[f.specIndex] ?? false)) {
      need("spec-off", "выбранная спецификация не отмечена к постановке на контроль.");
    }
  });

  const blocking = [...warnings, ...localWarnings].filter((w) => w.blocking);
  const informational = warnings.filter((w) => !w.blocking);

  function set<K extends keyof ContractForm>(key: K, value: ContractForm[K]): void {
    setForm((prev) => (prev === null ? prev : { ...prev, [key]: value }));
  }

  function changed<K extends keyof ContractForm>(key: K): boolean {
    return initial !== null && form !== null && form[key] !== initial[key];
  }

  function setSpec<K extends keyof SpecForm>(i: number, key: K, value: SpecForm[K]): void {
    setSpecForms((prev) => prev.map((s, j) => (j === i ? { ...s, [key]: value } : s)));
  }

  function setObl<K extends keyof OblForm>(i: number, key: K, value: OblForm[K]): void {
    setOblForms((prev) => prev.map((o, j) => (j === i ? { ...o, [key]: value } : o)));
  }

  /** Выбрать сторону договора контрагентом — заполняет поля реквизитов (постановка п. 2). */
  function pickParty(party: PrefillParty): void {
    setUseExisting("");
    setCpName(party.name ?? "");
    setCpInn(party.inn ?? "");
    setCpOgrn(party.ogrn ?? "");
  }

  /**
   * Тело подтверждения.
   *
   * ССЫЛКИ ПЕРЕСЧИТЫВАЮТСЯ В МЕСТА ОТПРАВЛЯЕМОГО СПИСКА. На экране юрист выбирает спецификацию и
   * триггер из ПОЛНОГО списка разбора, а уходит только отмеченное — и третье обязательство
   * списка становится, скажем, первым в отправке. Отправить исходный номер значило бы связать
   * обязательство не с тем: ошибка тихая, видна только по странному сроку в карточке.
   */
  function confirmBody() {
    const specPos = new Map<number, number>();
    const specs2 = specs
      .map((s, i) => ({ s, i }))
      .filter(({ i }) => specsTaken[i])
      .map(({ s, i }, pos) => {
        specPos.set(i, pos);
        return {
          number: specForms[i]?.number.trim() ?? "",
          date: specForms[i]?.date ?? "",
          amount: s.amount ?? undefined,
          subject: s.subject ?? undefined,
        };
      });

    const oblPos = new Map<number, number>();
    const takenObls = obls.map((_, i) => i).filter((i) => oblTaken[i]);
    takenObls.forEach((orig, pos) => oblPos.set(orig, pos));

    const obligations = takenObls.map((i) => {
      const f = oblForms[i]!;
      const o = obls[i]!;
      return {
        type: f.type,
        debtor: f.debtor as ObligationDebtor,
        descr: f.descr.trim(),
        amount: o.amount ?? undefined,
        ruleKind: f.ruleKind,
        ruleDate: f.ruleKind === "fixed" ? f.ruleDate : undefined,
        ruleDays: f.ruleKind === "fixed" ? undefined : Number(f.ruleDays),
        dayType: f.ruleKind === "fixed" ? undefined : f.dayType,
        ruleBase: f.ruleKind === "relative" ? f.ruleBase : undefined,
        specificationIndex: f.specIndex === null ? null : (specPos.get(f.specIndex) ?? null),
        triggerIndex:
          f.ruleKind === "event" && f.triggerIndex !== null
            ? (oblPos.get(f.triggerIndex) ?? null)
            : null,
      };
    });

    return {
      version: card.draft.updatedAt,
      counterparty:
        useExisting === ""
          ? {
              create: {
                name: cpName,
                inn: cpInn === "" ? undefined : cpInn,
                ogrn: cpOgrn === "" ? undefined : cpOgrn,
              },
            }
          : { id: useExisting },
      contract: {
        number: form!.number,
        date: form!.date,
        direction: form!.direction,
        subject: form!.subject === "" ? undefined : form!.subject,
        validityTerm: form!.validityTerm === "" ? undefined : form!.validityTerm,
        price: form!.price === "" ? undefined : form!.price,
        penPayRate: form!.penPayRate === "" ? undefined : form!.penPayRate,
        penDelRate: form!.penDelRate === "" ? undefined : form!.penDelRate,
        claimRequired: form!.claimRequired,
        claimDays: form!.claimDays === "" ? undefined : Number(form!.claimDays),
        court: form!.court === "" ? undefined : form!.court,
      },
      specifications: specs2,
      obligations,
    };
  }

  async function onConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await confirmDraft(card.draft.id, confirmBody());
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    navigate(`/contracts/${result.value.contractId}`);
  }

  /** «Взять в работу» и «Освободить» — оба просто перечитывают карточку (D-24). */
  async function onTake(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await takeDraftInWork(card.draft.id);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      // Отказ означает, что кто-то успел раньше, — перечитываем, чтобы показать чьё имя.
      await load();
      return;
    }
    await load();
  }

  async function onRelease(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await releaseDraftInWork(card.draft.id);
    setBusy(false);
    if (!result.ok) setError(result.message);
    await load();
  }

  async function onReject(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await rejectDraft(card.draft.id, rejectReason);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setRejectReason("");
    // Черновик решён — возвращаемся в очередь: на этой карточке работы больше нет.
    navigate("/verify");
  }

  return (
    <Shell>
      <DemoModeBanner notice={state.demoNotice} />

      <p className="mt-1 text-[13px] text-muted">
        <Link to="/verify" className="font-bold text-accent hover:underline">
          ← К списку черновиков
        </Link>
      </p>

      <p className="mt-1 text-[13px] text-muted">
        Разбор от {formatDateTime(card.draft.createdAt)}
        {card.draft.createdByName === null ? "" : `, отправил ${card.draft.createdByName}`}
      </p>

      {/* Захват — про очередь работы; у подтверждённого черновика работы нет. */}
      {!readOnly && (
        <InWorkBar
          draft={card.draft}
          me={user?.id ?? null}
          busy={busy}
          onTake={() => void onTake()}
          onRelease={() => void onRelease()}
        />
      )}

      {readOnly && (
        <div className="mt-3 rounded-[var(--radius-m)] border border-border bg-bg p-3">
          <p className="text-[14px] font-bold text-ink">
            {DRAFT_STATUS_LABEL[card.draft.status]} — договор поставлен на контроль
          </p>
          <p className="mt-1 max-w-[var(--w-text)] text-[13px] text-muted">
            Ниже — то, что предложила модель, и сам скан: поля закрыты, менять здесь нечего. Что
            встало на контроль, видно в карточке договора — правки вносятся там, в любой момент.
          </p>
          {card.draft.contractId !== null && (
            <Link
              to={`/contracts/${card.draft.contractId}`}
              className={`${btnSecondary} mt-3 inline-block`}
            >
              Открыть договор
            </Link>
          )}
        </div>
      )}

      {/*
        ДОКУМЕНТ И ПОЛЯ — РЯДОМ, В ДВУХ КОЛОНКАХ (D-47, план раздел 7.3 «Раскладка»).
        Слева скан (≈45 %), справа поля разбора (≈55 %) в колонке формы 720 px.

        Две колонки появляются только на ШИРОКОМ экране (от 1536 px). На 1366×768 и у́же они
        складываются в одну — панель просмотра сверху: делить 1366 пополам значит оставить полям
        полосу, в которой их не прочесть. Точка перелома `2xl` взята та же, что у сводки
        (`SummaryPage.tsx`), — вторая точка в проекте означала бы два разных «широкого экрана».
      */}
      <div className="mt-3 grid items-start gap-4 2xl:grid-cols-[45fr_55fr]">
        <ScanPanel draftId={card.draft.id} files={card.draft.files} />

        {/*
          ПОЛЯ ЗАКРЫВАЮТСЯ ЦЕЛИКОМ, А НЕ ПО ОДНОМУ. `fieldset disabled` гасит все поля и кнопки
          внутри силами браузера: перечислять их по одному значило бы завести список, который
          разойдётся с разметкой на первой же новой строке разбора — и разойдётся молча, открыв
          поле там, где его правка никуда не уйдёт.
        */}
        <fieldset disabled={readOnly} className="min-w-0 max-w-[var(--w-form)] border-0 p-0">
          {prefill !== null && prefill.notes.length > 0 && (
            <section
              aria-labelledby="notes-title"
              className="mt-5 rounded-[var(--radius-l)] border border-accent bg-control-bg p-4"
            >
              <h2 id="notes-title" className="text-[15px] font-bold text-ink">
                Замечания модели
              </h2>
              <ul className="mt-2 space-y-1.5 text-[14px] text-ink">
                {prefill.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </section>
          )}

          {/*
            Проверки — про то, что МЕШАЕТ поставить на контроль. У подтверждённого черновика
            ставить нечего, а замечания, посчитанные по исходному предложению модели, говорили бы
            о препятствиях там, где договор давно на контроле.
          */}
          {!readOnly && (blocking.length > 0 || informational.length > 0) && (
            <section aria-labelledby="warn-title" className="mt-5">
              <h2 id="warn-title" className="text-[15px] font-bold text-ink">
                Проверки
              </h2>
              <ul className="mt-2 space-y-1.5">
                {blocking.map((w) => (
                  <li key={w.code} role="alert" className="text-[14px] text-overdue-fg">
                    {w.message}
                  </li>
                ))}
                {informational.map((w) => (
                  <li key={w.code} className="text-[14px] text-muted">
                    {w.message}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {card.draft.files.some((f) => !f.sentToAi) && (
            <ul className="mt-3 space-y-1 text-[13px] text-overdue-fg">
              {card.draft.files
                .filter((f) => !f.sentToAi)
                .map((f) => (
                  <li key={f.id}>
                    «{f.originalName}» на разбор не отправлен
                    {f.skipReason === null ? "" : `: ${f.skipReason}`} — проверьте его по скану.
                  </li>
                ))}
            </ul>
          )}

          {/* ── 1. Контрагент ── */}
          <section aria-labelledby="cp-title" className="mt-6">
            <h2 id="cp-title" className="text-[15px] font-bold text-ink">
              Контрагент
            </h2>

            {prefill !== null && prefill.counterparty.name === null && (
              <p className="mt-1 max-w-[var(--w-text)] text-[13px] text-overdue-fg">
                Система не смогла определить, какая из сторон договора наша, а угадывать здесь
                нельзя — подставленный не тот контрагент виден только при сверке со сканом. Выберите
                контрагента из сторон ниже, из справочника или заполните поля сами.
              </p>
            )}

            {/*
          СТОРОНЫ ИЗ РАЗБОРА — ВЫБОРОМ, А НЕ ПЕРЕПЕЧАТЫВАНИЕМ (постановка 16.08.2026). Модель
          нашла обе стороны с реквизитами; раньше при неопознанной нашей стороне юрист видел три
          пустых поля и вбивал ИНН со скана глазами. Предохранитель «не угадываем» остаётся: ни
          одна сторона не выбрана заранее, нажимает юрист.
        */}
            {prefill !== null && prefill.parties.length > 0 && (
              <div className="mt-3">
                <p className="text-[13px] text-muted">
                  Стороны, найденные в документе, — выберите ту, которая нам контрагент:
                </p>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                  {prefill.parties.map((party, i) => (
                    <li
                      key={`${party.inn ?? party.name ?? "сторона"}-${i}`}
                      className="rounded-[var(--radius-m)] border border-border bg-surface p-3"
                    >
                      <p className="text-[14px] font-bold text-ink">
                        {party.name ?? "без наименования"}
                      </p>
                      {party.roleLabel !== null && (
                        <p className="text-[13px] text-muted">{party.roleLabel} по договору</p>
                      )}
                      <p className="num mt-1 text-[13px] text-muted">
                        {party.inn === null ? "ИНН не найден" : `ИНН ${party.inn}`}
                        {party.ogrn === null ? "" : `, ОГРН ${party.ogrn}`}
                      </p>
                      {party.matchesOrg && (
                        <p className="mt-1 text-[13px] font-bold text-done-fg">
                          Это наша организация — контрагентом её выбирать не нужно.
                        </p>
                      )}
                      <Citation text={party.citation} />
                      <button
                        type="button"
                        onClick={() => pickParty(party)}
                        className={`${btnSecondary} mt-2`}
                      >
                        Это контрагент
                      </button>
                    </li>
                  ))}
                </ul>
                {prefill.parties.every((p) => !p.matchesOrg) && (
                  <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-overdue-fg">
                    Ни одна из сторон не совпадает с нашей организацией — ни по ИНН, ни по
                    наименованию. Проверьте, наш ли это договор и верны ли реквизиты организации в
                    настройках; направление договора в таком случае тоже придётся указать вручную.
                  </p>
                )}
              </div>
            )}

            {prefill !== null && prefill.parties.length === 0 && (
              <Citation text={prefill.counterparty.citation} />
            )}

            <label className="mt-3 block text-[13px] text-muted" htmlFor="cp-existing">
              Выбрать из справочника
            </label>
            <select
              id="cp-existing"
              className={inputCls}
              value={useExisting}
              onChange={(e) => setUseExisting(e.target.value)}
            >
              <option value="">— завести нового по реквизитам из скана —</option>
              {counterparties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.inn === null ? "" : ` (ИНН ${c.inn})`}
                </option>
              ))}
            </select>

            {useExisting === "" && (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Наименование" id="cp-name">
                  <input
                    id="cp-name"
                    className={`${inputCls} ${cpName.trim() === "" ? missingCls : ""}`}
                    value={cpName}
                    onChange={(e) => setCpName(e.target.value)}
                  />
                </Field>
                <Field label="ИНН" id="cp-inn">
                  <input
                    id="cp-inn"
                    className={`num ${inputCls}`}
                    value={cpInn}
                    onChange={(e) => setCpInn(e.target.value)}
                  />
                </Field>
                <Field label="ОГРН" id="cp-ogrn">
                  <input
                    id="cp-ogrn"
                    className={`num ${inputCls}`}
                    value={cpOgrn}
                    onChange={(e) => setCpOgrn(e.target.value)}
                  />
                </Field>
              </div>
            )}
          </section>

          {/* ── 2. Карточка договора ── */}
          <section aria-labelledby="contract-title" className="mt-6">
            <h2 id="contract-title" className="text-[15px] font-bold text-ink">
              Договор
            </h2>
            <p className="mt-1 text-[13px] text-muted">{prefill?.directionExplanation}</p>
            {/*
              ЦИТАТА БЛОКА — ОДИН РАЗ, ПОД ЗАГОЛОВКОМ (замечание владельца 16.10.2026). Модель даёт
              одну цитату на все реквизиты договора; копия под каждым из шести полей читалась как
              шесть подтверждений, хотя относится в лучшем случае к одному. Отдельная цитата на
              поле — остаток D-68 (смена схемы ответа модели).
            */}
            {prefill !== null && <Citation text={prefill.contractCitation} />}

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Номер" id="c-number" cite={prefill?.number}>
                <input
                  id="c-number"
                  className={`${inputCls} ${form.number.trim() === "" ? missingCls : changed("number") ? changedCls : ""}`}
                  value={form.number}
                  onChange={(e) => set("number", e.target.value)}
                />
              </Field>
              <Field label="Дата" id="c-date" cite={prefill?.date}>
                <input
                  id="c-date"
                  type="date"
                  className={`num ${inputCls} ${form.date === "" ? missingCls : changed("date") ? changedCls : ""}`}
                  value={form.date}
                  onChange={(e) => set("date", e.target.value)}
                />
              </Field>
              <Field label="Направление" id="c-direction" cite={prefill?.direction}>
                <select
                  id="c-direction"
                  className={`${inputCls} ${form.direction === "" ? missingCls : changed("direction") ? changedCls : ""}`}
                  value={form.direction}
                  onChange={(e) => set("direction", e.target.value)}
                >
                  <option value="">— выберите —</option>
                  <option value="sale">Мы поставщик</option>
                  <option value="purchase">Мы покупатель</option>
                </select>
              </Field>
              <Field label="Цена договора, ₽" id="c-price" cite={prefill?.price}>
                <input
                  id="c-price"
                  className={`num ${inputCls} ${changed("price") ? changedCls : ""}`}
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                />
              </Field>
              <Field label="Подсудность" id="c-court" cite={prefill?.court}>
                <input
                  id="c-court"
                  className={`${inputCls} ${changed("court") ? changedCls : ""}`}
                  value={form.court}
                  onChange={(e) => set("court", e.target.value)}
                />
              </Field>
              <Field label="Срок действия" id="c-term" cite={prefill?.validityTerm}>
                <input
                  id="c-term"
                  className={`${inputCls} ${changed("validityTerm") ? changedCls : ""}`}
                  value={form.validityTerm}
                  onChange={(e) => set("validityTerm", e.target.value)}
                />
              </Field>
            </div>

            {/*
              НЕУСТОЙКА — ПОДЗАГОЛОВКОМ ВНУТРИ «ДОГОВОРА», со своей цитатой блока (замечание
              владельца 16.10.2026). Уровень h3, а не h2: разделы экрана — контрагент, договор,
              спецификации, обязательства — порядок их h2 сторожат тесты, а ставки остаются частью
              карточки договора.
            */}
            <div role="group" aria-labelledby="penalties-title" className="mt-5">
              <h3 id="penalties-title" className="text-[14px] font-bold text-ink">
                Неустойка
              </h3>
              {prefill !== null && <Citation text={prefill.penaltiesCitation} />}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field
                  label="Неустойка за просрочку оплаты, % в день"
                  id="c-pay"
                  cite={prefill?.penPayRate}
                >
                  <input
                    id="c-pay"
                    className={`num ${inputCls} ${changed("penPayRate") ? changedCls : ""}`}
                    value={form.penPayRate}
                    onChange={(e) => set("penPayRate", e.target.value)}
                  />
                </Field>
                <Field
                  label="Неустойка за просрочку поставки, % в день"
                  id="c-del"
                  cite={prefill?.penDelRate}
                >
                  <input
                    id="c-del"
                    className={`num ${inputCls} ${changed("penDelRate") ? changedCls : ""}`}
                    value={form.penDelRate}
                    onChange={(e) => set("penDelRate", e.target.value)}
                  />
                </Field>
              </div>

              {prefill?.penCapPercent.note !== null && prefill !== null && (
                <p className="mt-3 text-[13px] text-muted">{prefill.penCapPercent.note}</p>
              )}
            </div>

            <label className="mt-4 flex items-center gap-2 text-[14px] text-ink">
              <input
                type="checkbox"
                checked={form.claimRequired}
                onChange={(e) => set("claimRequired", e.target.checked)}
              />
              Досудебный (претензионный) порядок обязателен
            </label>
            {/* Под пустым полем цитаты нет — то же правило, что у `Field`. */}
            {prefill !== null && prefill.claimRequired.value !== null && (
              <Citation text={prefill.claimRequired.citation} />
            )}
          </section>

          {/* ── 3. Спецификации ДО обязательств ── */}
          <section aria-labelledby="specs-title" className="mt-6">
            <h2 id="specs-title" className="text-[15px] font-bold text-ink">
              Спецификации
            </h2>
            {specs.length === 0 ? (
              <p className="mt-2 text-[13px] text-muted">Модель спецификаций не нашла.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {specs.map((s, i) => {
                  const taken = specsTaken[i] ?? false;
                  const f = specForms[i] ?? { number: "", date: "" };
                  return (
                    <li
                      key={`spec-${i}`}
                      className="rounded-[var(--radius-m)] border border-border p-3"
                    >
                      <label className="flex items-start gap-2 text-[14px] text-ink">
                        <input
                          type="checkbox"
                          checked={taken}
                          onChange={(e) =>
                            setSpecsTaken((prev) =>
                              prev.map((v, j) => (j === i ? e.target.checked : v)),
                            )
                          }
                        />
                        <span>
                          {s.subject ?? "спецификация"}
                          {s.amount === null ? "" : ` — ${formatMoney(s.amount)}`}
                        </span>
                      </label>

                      {/*
                    НОМЕР И ДАТА — ПОЛЯМИ, А НЕ ТЕКСТОМ. Оба обязательны с E5, и оба модель
                    находит не всегда: на приёмке 16.08.2026 дата пришла пустой, а вписать её
                    было некуда — отсюда и отказ на кнопке.
                  */}
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <Field label="Номер спецификации" id={`spec-num-${i}`}>
                          <input
                            id={`spec-num-${i}`}
                            className={`${inputCls} ${taken && f.number.trim() === "" ? missingCls : ""}`}
                            value={f.number}
                            onChange={(e) => setSpec(i, "number", e.target.value)}
                          />
                        </Field>
                        <Field label="Дата спецификации" id={`spec-date-${i}`}>
                          <input
                            id={`spec-date-${i}`}
                            type="date"
                            className={`num ${inputCls} ${taken && f.date === "" ? missingCls : ""}`}
                            value={f.date}
                            onChange={(e) => setSpec(i, "date", e.target.value)}
                          />
                        </Field>
                      </div>

                      {s.note !== null && (
                        <p className="mt-1 text-[13px] text-overdue-fg">{s.note}</p>
                      )}
                      <Citation text={s.citation} />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── 4. Обязательства — подсказкой ── */}
          <section aria-labelledby="obl-title" className="mt-6">
            <h2 id="obl-title" className="text-[15px] font-bold text-ink">
              Обязательства — предложение модели
            </h2>
            <p className="mt-1 max-w-[var(--w-text)] text-[13px] text-muted">
              Отметьте те, которые ставим на контроль, и проверьте у каждого срок. Правило приведено
              так, как его даёт цитата; за раскладку и сроки отвечает верифицирующий юрист.
            </p>
            {obls.length === 0 ? (
              <p className="mt-2 text-[13px] text-muted">Модель обязательств не предложила.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {obls.map((o, i) => (
                  <ObligationRow
                    key={`obl-${i}`}
                    index={i}
                    parsed={o}
                    form={oblForms[i] ?? initialObl(o, specs.length)}
                    taken={oblTaken[i] ?? false}
                    specs={specs.map((s, j) => ({
                      index: j,
                      label: specName(specForms[j]?.number ?? s.number ?? "", j),
                      taken: specsTaken[j] ?? false,
                    }))}
                    others={obls
                      .map((_, j) => ({
                        index: j,
                        label: oblName(oblForms[j]?.descr ?? "", j),
                        taken: oblTaken[j] ?? false,
                      }))
                      .filter((x) => x.index !== i)}
                    onToggle={(v) =>
                      setOblTaken((prev) => prev.map((old, j) => (j === i ? v : old)))
                    }
                    onChange={(key, value) => setObl(i, key, value)}
                  />
                ))}
              </ul>
            )}
          </section>

          {error !== null && (
            <p role="alert" className="mt-4 text-[14px] text-overdue-fg">
              {error}
            </p>
          )}

          {/*
            Решения — только у неподтверждённого черновика. Закрытая кнопка тут была бы обещанием
            действия, которого больше нет: подтверждение необратимо, а отклонять поставленный на
            контроль договор нечем — юрист правит договор.
          */}
          {!readOnly && (
            <div className="mt-6 flex flex-wrap items-end gap-3">
              <button
                type="button"
                onClick={() => void onConfirm()}
                disabled={busy || blocking.length > 0 || heldByOther}
                className={btnPrimary}
              >
                Подтвердить и поставить на контроль
              </button>
              {blocking.length > 0 && !heldByOther && (
                <span className="text-[13px] text-overdue-fg">
                  Подтверждение недоступно, пока есть препятствие выше.
                </span>
              )}
            </div>
          )}

          {!readOnly && (
            <section aria-labelledby="reject-title" className="mt-6">
              <h2 id="reject-title" className="text-[15px] font-bold text-ink">
                Отклонить черновик
              </h2>
              <label className="mt-1 block text-[13px] text-muted" htmlFor="reject-reason">
                Причина — её прочитает тот, кто будет переделывать (обязательна)
              </label>
              <textarea
                id="reject-reason"
                className={`${inputCls} mt-1`}
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={heldByOther}
              />
              <button
                type="button"
                onClick={() => void onReject()}
                disabled={busy || rejectReason.trim() === "" || heldByOther}
                className={`${btnSecondary} mt-2`}
              >
                Отклонить
              </button>
            </section>
          )}
        </fieldset>
      </div>
    </Shell>
  );
}

/**
 * ПАНЕЛЬ ПРОСМОТРА СКАНА (D-47, эпик E17) — план docs/plans/E17-initsiator-dogovora.md,
 * раздел 7.3 «Раскладка».
 *
 * ЗАЧЕМ. До эпика E17 раскладку подтверждал юрист, который договор и так читал. После E17 её
 * подтверждает Инициатор — человек без юридической подготовки, и сверять поля по памяти о
 * документе он не может. Сканы были ссылками, открывавшими файл в другой вкладке: чтобы сличить
 * номер спецификации, приходилось переключаться туда-обратно на каждое поле.
 *
 * ЧТО ПОКАЗЫВАЕМ. PDF, JPG и PNG — прямо в странице. DOCX показать нельзя (браузер его не
 * отображает), и вместо пустой рамки даётся честное пояснение и кнопка «Скачать файл»:
 * преобразование DOCX в картинку — серверная работа, которая не окупается, пока не видно, как
 * часто заказчик присылает этот формат (решение плана, раздел 7.3).
 *
 * ТИП БЕРЁТСЯ ИЗ `contentType`, который вернул сервер, а НЕ угадывается по расширению имени:
 * имя приходит от человека и врёт («договор.pdf», внутри картинка), а `contentType` сервер
 * определил по содержимому при загрузке.
 */
/**
 * Широкая ли раскладка сейчас — та же точка перелома, что у сетки `2xl:grid-cols-[45fr_55fr]`
 * (1536 px, Tailwind `2xl`): ниже неё колонки складываются в одну. Значение берётся один раз при
 * открытии экрана — дальше состоянием панели распоряжается человек кнопкой.
 *
 * Среда без `matchMedia` (тесты в jsdom) считается широкой: там ширины нет вовсе, и прятать
 * содержимое панели значило бы прятать его от всех проверок.
 */
function wideLayout(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(min-width: 1536px)").matches;
}

function ScanPanel({ draftId, files }: { draftId: string; files: readonly DraftFileView[] }) {
  // По умолчанию открыт первый файл, УШЕДШИЙ НА РАЗБОР: именно его модель и читала, и именно с
  // ним сверяются предложенные поля. Не влезшие в предел вложения лежат ниже по списку.
  const preferred = files.find((f) => f.sentToAi) ?? files[0];
  const [openId, setOpenId] = useState<string | null>(preferred?.id ?? null);
  /*
   * ПАНЕЛЬ СВОРАЧИВАЕТСЯ, И НА НОУТБУКЕ ОТКРЫВАЕТСЯ СВЁРНУТОЙ (решение плана, раздел 7.3
   * «Раскладка»: иначе «на поля остаётся полоса, в которой их не прочесть»). На широком мониторе
   * документ и поля стоя́т рядом — панель открыта, сворачивать незачем. На узком экране колонки
   * складываются в одну, и раскрытая панель занимает первый экран целиком: человек попадает на
   * страницу проверки и не видит НИ ОДНОГО поля. Поэтому начальное состояние берётся с той же
   * точки перелома, что и сама раскладка.
   */
  const [open, setOpen] = useState(wideLayout);
  const current = files.find((f) => f.id === openId) ?? preferred;

  if (current === undefined) return null;

  const viewable = VIEWABLE_TYPES.has(current.contentType);

  return (
    <section aria-labelledby="scan-title" className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <h2 id="scan-title" className="text-[15px] font-bold text-ink">
          Документ
        </h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="scan-body"
          className="text-[13px] font-bold text-accent hover:underline"
        >
          {open ? "Свернуть" : "Развернуть"}
        </button>
      </div>

      <div id="scan-body" hidden={!open}>
        {/* Переключатель файлов комплекта. Одного файла — переключать нечего, и ряда нет. */}
        {files.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {files.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setOpenId(f.id)}
                aria-pressed={f.id === current.id}
                className={f.id === current.id ? btnPrimary : btnSecondary}
              >
                {f.originalName}
              </button>
            ))}
          </div>
        )}

        <div className="mt-2 rounded-[var(--radius-l)] border border-border bg-surface p-2">
          {viewable ? (
            <iframe
              key={current.id}
              title={`Скан: ${current.originalName}`}
              src={draftFileViewUrl(draftId, current.id)}
              className="h-[52vh] min-h-80 w-full rounded-[var(--radius-m)] border border-border bg-bg 2xl:h-[70vh]"
            />
          ) : (
            <div className="p-4">
              <p className="text-[14px] text-ink">
                Файл «{current.originalName}» — документ Word. Показать его прямо здесь браузер не
                умеет, поэтому откройте его отдельно.
              </p>
              <a
                href={draftFileUrl(draftId, current.id)}
                target="_blank"
                rel="noreferrer"
                className={`${btnSecondary} mt-3 inline-block`}
              >
                Скачать файл
              </a>
            </div>
          )}
        </div>

        <p className="mt-2 text-[13px] text-muted">
          {current.sentToAi
            ? "Этот файл модель читала — поля справа предложены по нему."
            : "Этот файл модель НЕ читала" +
              (current.skipReason === null ? "" : `: ${current.skipReason}`) +
              " — поля справа по нему не заполнялись."}{" "}
          <a
            href={draftFileUrl(draftId, current.id)}
            target="_blank"
            rel="noreferrer"
            className="font-bold text-accent hover:underline"
          >
            Скачать
          </a>
        </p>
      </div>
    </section>
  );
}

/**
 * Полоса захвата: кто держит черновик и что с этим делать (D-24, пункт «г»).
 *
 * ТРИ СОСТОЯНИЯ, И КАЖДОЕ ОБЪЯСНЯЕТ СЕБЯ СЛОВАМИ, а не наличием кнопок:
 *   • свободен — «Взять в работу», и рядом сказано, зачем это (чтобы коллега видел);
 *   • у меня — «В работе — Вы, с ЧЧ:ММ» и «Освободить»;
 *   • у другого — имя, время и прямая фраза, что решения закрыты, но читать можно.
 *
 * Кнопки — ПОДСКАЗКА, а не защита: тот же запрет держит сервер (правило 5). Смысл полосы в том,
 * чтобы человек узнал о занятости ДО того, как проверил полсотни полей, а не из отказа на кнопке.
 */
function InWorkBar({
  draft,
  me,
  busy,
  onTake,
  onRelease,
}: {
  draft: DraftView;
  me: string | null;
  busy: boolean;
  onTake: () => void;
  onRelease: () => void;
}) {
  if (draft.inWorkById === null) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-[var(--radius-m)] border border-border bg-bg p-3">
        <button type="button" onClick={onTake} disabled={busy} className={btnSecondary}>
          Взять в работу
        </button>
        <span className="text-[13px] text-muted">
          Пока черновик не взят, его может править и подтвердить любой юрист. Возьмите в работу — и
          коллеги увидят, что им занимаетесь Вы.
        </span>
      </div>
    );
  }

  if (draft.inWorkById === me) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-[var(--radius-m)] border border-border bg-bg p-3">
        <InWorkStamp name={null} since={draft.inWorkSince} you />
        <button type="button" onClick={onRelease} disabled={busy} className={btnSecondary}>
          Освободить
        </button>
        <span className="text-[13px] text-muted">
          Если долго не работать с черновиком, он освободится сам.
        </span>
      </div>
    );
  }

  return (
    <div role="status" className="mt-3 rounded-[var(--radius-m)] border border-border bg-bg p-3">
      <InWorkStamp name={draft.inWorkByName} since={draft.inWorkSince} />
      <p className="mt-1.5 max-w-[var(--w-text)] text-[13px] text-ink">
        Разбор можно смотреть и сверять со сканом, но подтвердить или отклонить его сейчас нельзя —
        обратитесь к коллеге. Если он не закончит, черновик освободится сам.{" "}
        <HelpLink topic="draftHeldByOther" />
      </p>
    </div>
  );
}

/**
 * Карточка черновика, который не готов к верификации: разбор идёт, упал или уже решён (D-24).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ВИД, А НЕ «ЧЕРНОВИКОВ НЕТ». Сюда приходят по прямой ссылке — из очереди и из
 * сигнала колокольчика. Ответ «черновиков на верификацию нет» на такой переход был бы прямой
 * неправдой: черновик есть, и человек только что нажал именно на него. Состояние называется, а
 * рядом стоит ровно то действие, которое из него ведёт, — у «ошибки» их два, и это главный
 * выход из тупика, ради которого задача заводилась.
 */
function NotReady({
  card,
  mine,
  busy,
  error,
  reason,
  onReason,
  onRetry,
  onWithdraw,
}: {
  card: DraftCard;
  /** Комплект отправил тот, кто смотрит: своё отзывается в любом незакрытом состоянии. */
  mine: boolean;
  busy: boolean;
  error: string | null;
  reason: string;
  onReason: (value: string) => void;
  onRetry: () => void;
  onWithdraw: () => void;
}) {
  const draft = card.draft;
  return (
    <>
      <p className="mt-1 text-[13px] text-muted">
        <Link to="/verify" className="font-bold text-accent hover:underline">
          ← К списку черновиков
        </Link>
      </p>

      <p className="mt-3 text-[15px] font-bold text-ink">{draftStatusLabel(draft)}</p>
      <p className="mt-1 max-w-[var(--w-text)] text-[14px] text-muted">
        {DRAFT_STATUS_HINT[draft.status]}
      </p>
      <p className="mt-2 text-[13px] text-muted">
        Комплект от {formatDateTime(draft.createdAt)}
        {draft.createdByName === null ? "" : `, отправил ${draft.createdByName}`}:{" "}
        {draft.files.map((f) => f.originalName).join(", ")}
      </p>

      {draft.status === "failed" && draft.errorText !== null && (
        <p className="mt-3 max-w-[var(--w-text)] text-[14px] text-overdue-fg">{draft.errorText}</p>
      )}
      {draft.status === "rejected" && draft.rejectReason !== null && (
        <p className="mt-3 max-w-[var(--w-text)] text-[14px] text-muted">
          Причина отклонения: {draft.rejectReason}
        </p>
      )}
      {draft.status === "withdrawn" && draft.withdrawReason !== null && (
        <p className="mt-3 max-w-[var(--w-text)] text-[14px] text-muted">
          Отозвал {draft.withdrawnByName ?? "—"}: {draft.withdrawReason}
        </p>
      )}
      {draft.status === "confirmed" && draft.contractId !== null && (
        <Link to={`/contracts/${draft.contractId}`} className={`${btnSecondary} mt-4 inline-block`}>
          Открыть договор
        </Link>
      )}

      {error !== null && (
        <p role="alert" className="mt-3 text-[14px] text-overdue-fg">
          {error}
        </p>
      )}

      {canRetryParsing(draft) && (
        <button type="button" onClick={onRetry} disabled={busy} className={`${btnSecondary} mt-4`}>
          Повторить разбор
        </button>
      )}

      {/*
        Выход есть у любого нерешённого состояния, а не только у «ошибки»: комплект, который ещё
        разбирается, тоже бывает загружен по ошибке.

        Но ЧУЖОЕ отзывается только упавшее: чужой готовый разбор юрист закрывает отклонением —
        он его посмотрел и вынес суждение. Кнопка показывается там, где отзыв пройдёт; настоящий
        запрет держит сервер (правило 5).
      */}
      {isWithdrawable(draft.status) && (mine || draft.status === "failed") && (
        <section aria-labelledby="withdraw-title" className="mt-6">
          <h2 id="withdraw-title" className="text-[15px] font-bold text-ink">
            Отозвать комплект
          </h2>
          <label className="mt-1 block text-[13px] text-muted" htmlFor="withdraw-reason">
            Причина — она останется в журнале вместо записи (обязательна)
          </label>
          <textarea
            id="withdraw-reason"
            className={`${inputCls} mt-1`}
            rows={2}
            value={reason}
            onChange={(e) => onReason(e.target.value)}
          />
          <button
            type="button"
            onClick={onWithdraw}
            disabled={busy || reason.trim() === ""}
            className={`${btnSecondary} mt-2`}
          >
            Отозвать
          </button>
          <p className="mt-2 max-w-[var(--w-text)] text-[13px] text-muted">
            Отзыв закрывает строку в очереди — файлы при этом сохраняются, а причина остаётся в
            журнале. Договор на контроль не встаёт.
          </p>
        </section>
      )}
    </>
  );
}

/*
 * `settledNotice` убран в D-24. Он объяснял, почему экран показывает НЕ ТОТ черновик, за которым
 * пришли по ссылке («тот уже подтверждён — вот вам первый готовый»). С адресом `/verification/:id`
 * подменять объект больше не приходится: карточка открывает именно запрошенный черновик и просто
 * называет его состояние (см. `NotReady`).
 */

/** Имя спецификации — то же, каким её называет отказ сервера. */
function specName(number: string, index: number): string {
  const text = number.trim();
  return text === "" ? `Спецификация ${index + 1}-я в списке` : `Спецификация № ${text}`;
}

/** Имя обязательства — описанием, если оно есть, иначе местом в списке. */
function oblName(descr: string, index: number): string {
  const text = descr.trim();
  return text === "" ? `Обязательство ${index + 1}-е в списке` : `«${text}»`;
}

interface RowRef {
  readonly index: number;
  readonly label: string;
  readonly taken: boolean;
}

interface ObligationRowProps {
  readonly index: number;
  readonly parsed: PrefillObligation;
  readonly form: OblForm;
  readonly taken: boolean;
  readonly specs: readonly RowRef[];
  readonly others: readonly RowRef[];
  readonly onToggle: (value: boolean) => void;
  readonly onChange: <K extends keyof OblForm>(key: K, value: OblForm[K]) => void;
}

/**
 * Строка обязательства: предложение модели + поля, которыми юрист доводит его до постановки
 * на контроль.
 *
 * ПОЛЯ ВИДНЫ ВСЕГДА, А КРАСНЫМИ СТАНОВЯТСЯ ТОЛЬКО У ОТМЕЧЕННЫХ. Прятать их до галочки заманчиво —
 * экран короче, — но тогда юрист узнаёт, чего не хватает, ПОСЛЕ решения взять обязательство на
 * контроль, а решение это он принимает как раз по полноте срока. Тревожить же незаполненностью
 * строки, которую решили не брать, незачем: отсюда «видно всегда, красное — по отметке».
 */
function ObligationRow(props: ObligationRowProps) {
  const { index, parsed, form, taken, specs, others, onToggle, onChange } = props;
  const missing = (empty: boolean) => (taken && empty ? missingCls : "");

  return (
    <li className="rounded-[var(--radius-m)] border border-border p-3">
      <label className="flex items-start gap-2 text-[14px] text-ink">
        <input type="checkbox" checked={taken} onChange={(e) => onToggle(e.target.checked)} />
        <span>{parsed.descr ?? "без описания"}</span>
      </label>

      {/* Предложение модели остаётся видимым и после раскрытия полей: юрист сверяет с ним. */}
      <p className="mt-1 text-[13px] text-muted">{describeParsedRule(parsed)}</p>
      {parsed.deadlineHint !== null && (
        <p className="mt-1 text-[13px] text-ink">Подсказка: {parsed.deadlineHint}</p>
      )}
      {parsed.triggerDescription !== null && (
        <p className="mt-1 text-[13px] text-muted">Событие-триггер: {parsed.triggerDescription}</p>
      )}
      <Citation text={parsed.citation} />

      <div className="mt-3 border-t border-border-soft pt-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor={`obl-descr-${index}`}>
              Описание
            </label>
            <input
              id={`obl-descr-${index}`}
              className={`mt-1 ${inputCls} ${missing(form.descr.trim() === "")}`}
              value={form.descr}
              onChange={(e) => onChange("descr", e.target.value)}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor={`obl-type-${index}`}>
              Тип обязательства
            </label>
            <select
              id={`obl-type-${index}`}
              className={`mt-1 ${inputCls} ${missing(form.type === "")}`}
              value={form.type}
              onChange={(e) => onChange("type", e.target.value as ObligationType | "")}
            >
              <option value="">— выберите —</option>
              {(Object.keys(OBLIGATION_TYPE_LABELS) as ObligationType[]).map((t) => (
                <option key={t} value={t}>
                  {OBLIGATION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor={`obl-debtor-${index}`}>
              Должник
            </label>
            <select
              id={`obl-debtor-${index}`}
              className={`mt-1 ${inputCls} ${missing(form.debtor === "")}`}
              value={form.debtor}
              onChange={(e) => onChange("debtor", e.target.value as ObligationDebtor | "")}
            >
              <option value="">— выберите —</option>
              <option value="we">{DEBTOR_LABELS.we}</option>
              <option value="they">{DEBTOR_LABELS.they}</option>
            </select>
            {/*
              ТОЛЬКО У ОТМЕЧЕННОГО (замечание приёмки 16.08.2026). Обязательств в предложении
              бывает с десяток, и красный текст у каждого превращает экран в сплошную тревогу —
              за которой теряется строка, где препятствие настоящее. Пока обязательство не
              отмечено, оно никуда не идёт, и требовать от него полноты не за что; отметил —
              подсказка появляется сразу, вместе с подсветкой поля и строкой в «Проверках».
            */}
            {taken &&
              form.debtor === "" &&
              (parsed.debtor === null || parsed.debtor === "unknown") && (
                <p className="mt-1 text-[13px] text-overdue-fg">
                  модель не определила — укажите сами
                </p>
              )}
          </div>

          {specs.length > 0 && (
            <div>
              <label className={labelCls} htmlFor={`obl-spec-${index}`}>
                Спецификация обязательства
              </label>
              <select
                id={`obl-spec-${index}`}
                className={`mt-1 ${inputCls}`}
                value={form.specIndex === null ? "" : String(form.specIndex)}
                onChange={(e) =>
                  onChange("specIndex", e.target.value === "" ? null : Number(e.target.value))
                }
              >
                <option value="">— по договору в целом —</option>
                {specs.map((s) => (
                  <option key={s.index} value={s.index}>
                    {s.label}
                    {s.taken ? "" : " (не отмечена)"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <fieldset className="mt-3 rounded-[var(--radius-m)] border border-border-soft p-3">
          <legend className="px-1 text-[13px] text-muted">Правило срока</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor={`obl-rulekind-${index}`}>
                Вид правила
              </label>
              <select
                id={`obl-rulekind-${index}`}
                className={`mt-1 ${inputCls} ${missing(form.ruleKind === "")}`}
                value={form.ruleKind}
                onChange={(e) => onChange("ruleKind", e.target.value as ObligationRuleKind | "")}
              >
                <option value="">— выберите —</option>
                <option value="fixed">{RULE_KIND_LABELS.fixed}</option>
                <option value="relative">{RULE_KIND_LABELS.relative}</option>
                <option value="event">{RULE_KIND_LABELS.event}</option>
              </select>
            </div>

            {form.ruleKind === "fixed" && (
              <div>
                <label className={labelCls} htmlFor={`obl-ruledate-${index}`}>
                  Дата срока
                </label>
                <input
                  id={`obl-ruledate-${index}`}
                  type="date"
                  className={`num mt-1 ${inputCls} ${missing(form.ruleDate === "")}`}
                  value={form.ruleDate}
                  onChange={(e) => onChange("ruleDate", e.target.value)}
                />
              </div>
            )}

            {form.ruleKind === "relative" && (
              <div>
                <label className={labelCls} htmlFor={`obl-rulebase-${index}`}>
                  Отсчёт от даты
                </label>
                <select
                  id={`obl-rulebase-${index}`}
                  className={`mt-1 ${inputCls} ${missing(form.ruleBase === "")}`}
                  value={form.ruleBase}
                  onChange={(e) => onChange("ruleBase", e.target.value as ObligationRuleBase | "")}
                >
                  <option value="">— выберите —</option>
                  <option value="contract">{RULE_BASE_LABELS.contract}</option>
                  <option value="spec">{RULE_BASE_LABELS.spec}</option>
                </select>
              </div>
            )}

            {form.ruleKind === "event" && (
              <div>
                <label className={labelCls} htmlFor={`obl-trigger-${index}`}>
                  Обязательство-триггер
                </label>
                <select
                  id={`obl-trigger-${index}`}
                  className={`mt-1 ${inputCls} ${missing(form.triggerIndex === null)}`}
                  value={form.triggerIndex === null ? "" : String(form.triggerIndex)}
                  onChange={(e) =>
                    onChange("triggerIndex", e.target.value === "" ? null : Number(e.target.value))
                  }
                >
                  <option value="">— выберите обязательство —</option>
                  {others.map((x) => (
                    <option key={x.index} value={x.index}>
                      {x.label}
                      {x.taken ? "" : " (не отмечено)"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.ruleKind !== "" && form.ruleKind !== "fixed" && (
              <>
                <div>
                  <label className={labelCls} htmlFor={`obl-ruledays-${index}`}>
                    Число дней
                  </label>
                  <input
                    id={`obl-ruledays-${index}`}
                    type="number"
                    min={0}
                    className={`num mt-1 ${inputCls} ${missing(form.ruleDays.trim() === "")}`}
                    value={form.ruleDays}
                    onChange={(e) => onChange("ruleDays", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor={`obl-daytype-${index}`}>
                    Тип дней
                  </label>
                  <select
                    id={`obl-daytype-${index}`}
                    className={`mt-1 ${inputCls} ${missing(form.dayType === "")}`}
                    value={form.dayType}
                    onChange={(e) => onChange("dayType", e.target.value as ObligationDayType | "")}
                  >
                    <option value="">— выберите —</option>
                    <option value="calendar">{DAY_TYPE_LABELS.calendar}</option>
                    <option value="work">{DAY_TYPE_LABELS.work}</option>
                    <option value="bank">{DAY_TYPE_LABELS.bank}</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </fieldset>
      </div>
    </li>
  );
}

function Shell({ children, busy = false }: { children: React.ReactNode; busy?: boolean }) {
  return (
    <section aria-labelledby="verify-title" aria-busy={busy ? "true" : undefined}>
      <h1 id="verify-title" className="text-[20px] font-bold text-ink">
        Верификация
      </h1>
      {children}
    </section>
  );
}

/**
 * Правило срока СЛОВАМИ. Внутренние коды («relative», «event») на экран не выносятся: юристу они
 * ничего не говорят, а выглядят как сбой — то же правило, по которому с экрана убрали имена
 * настроек. Подписи берутся общие с реестром обязательств, чтобы формулировки не разошлись.
 */
function describeParsedRule(o: PrefillObligation): string {
  if (o.ruleKind === null) return "Правило срока не извлечено — задайте его сами.";
  if (o.ruleKind === "fixed") {
    // Дата — ДД.ММ.ГГГГ (правило 8). «до 2026-08-14» на экране юриста это внутренний формат
    // хранения, и он ровно из того же семейства, что «relative, 5 дн.», убранное на приёмке E11.
    return o.ruleDate === null ? "Срок: до конкретной даты" : `Срок: до ${formatDate(o.ruleDate)}`;
  }
  const days = o.ruleDays === null ? "?" : String(o.ruleDays);
  const dayType = o.dayType === null ? "" : `${DAY_TYPE_LABELS[o.dayType as ObligationDayType]} `;
  if (o.ruleKind === "relative") {
    const base =
      o.ruleBase === null ? "договора" : RULE_BASE_LABELS[o.ruleBase as ObligationRuleBase];
    return `Срок: ${days} ${dayType}дн. с даты ${base}`;
  }
  return `Срок: ${days} ${dayType}дн. с события`;
}

/**
 * Цитата из документа — то, с чем юрист сверяет значение.
 *
 * Курсивом и своим цветом (`.cite`, токен `--color-cite`) — постановка владельца 16.08.2026. Под
 * полями стоят два разных вида текста: «взято из документа» и подсказка системы, и раньше оба
 * были одинаково серыми. Различать их приходилось по началу фразы, а вопрос «это написано в
 * договоре или это нам так кажется» здесь главный.
 *
 * ПОДПИСЬ «Из документа:» — НЕ ЧАСТЬ ЦИТАТЫ (замечание приёмки 16.08.2026). Она наша, служебная, и
 * набирается как все прочие подписи. Курсив и цвет «как ручка» несут ровно одно значение: «эти
 * слова взяты из документа дословно». Распространив их на подпись, мы этим же начертанием
 * сказали бы, что и она из документа, — а её там нет.
 */
function Citation({ text }: { text: string | null }) {
  if (text === null || text.trim() === "") return null;
  return (
    <p className="mt-1 text-[12px] text-muted">
      Из документа: <span className="cite">«{text}»</span>
    </p>
  );
}

/*
 * Пометки «Модель здесь не уверена» (E11b, PR-4) на экране больше нет — снята решением владельца
 * 16.10.2026: статический список полей создавал ложное ощущение, что остальные поля проверены.
 */

interface FieldProps {
  readonly label: string;
  readonly id: string;
  readonly cite?: PrefillField<string | number | boolean> | undefined;
  readonly children: React.ReactNode;
}

/**
 * Поле формы с тем, что о нём сказал разбор.
 *
 * ПОД ПУСТЫМ ПОЛЕМ ЦИТАТЫ НЕТ (замечание владельца 16.10.2026): «Из документа: …» под полем без
 * значения обещает подтверждение тому, чего нет. Причина несведения (`note`) показывается и у
 * пустого поля — она объясняет, почему оно пустое.
 */
function Field({ label, id, cite, children }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {cite !== undefined && cite.value !== null && <Citation text={cite.citation} />}
      {/*
        ПРОВЕРЕНО ДВАЖДЫ (E11b PR-6): поле пусто и после досылки — модель на повторной проверке
        прямо ответила, что условия в документах нет. Это не «не нашли», а «нет»: юрист решает,
        заполнять ли поле, а не ищет его в скане заново. Служебная подпись — без курсива цитаты.
      */}
      {cite?.value === null && cite.absentConfirmed === true && (
        <p className="mt-1 text-[12px] text-muted" data-testid="absent-confirmed">
          В договоре не найдено (проверено дважды)
        </p>
      )}
      {cite?.note != null && <p className="mt-1 text-[13px] text-overdue-fg">{cite.note}</p>}
    </div>
  );
}
